import React from "react";
import { WorkoutRoutine, Exercise, User } from "../../api/types.ts";
import { RoutineCard } from "./RoutineCard.tsx";
import { useDialog } from "../DialogManager.tsx";
import { Banner } from "../widgets/Banner.tsx";

interface RoutineListProps {
  friends: Partial<User>[] | undefined;
  isQueried: boolean;
  untilLimit: number;
  routines: WorkoutRoutine[];
  exercises: Exercise[];
  preferredVisibility?: "friends" | "private";
}

const RoutineList: React.FC<RoutineListProps> = ({
  friends,
  isQueried,
  untilLimit,
  routines,
  exercises,
  preferredVisibility,
}) => {
  const { openDialog } = useDialog();

  // Create a map for quick lookup
  const exercisesMap = React.useMemo(() => {
    const exerciseMap = new Map<string, Exercise>();
    exercises?.forEach((ex) => exerciseMap.set(ex.id, ex));
    return exerciseMap;
  }, [exercises]);

  return isQueried && routines.length === 0 ? (
    <p className="text-gray-500 mb-4">Found no matching routines.</p>
  ) : routines.length === 0 ? (
    <div className="text-center py-12">
      <p className="text-gray-500 mb-4">
        You don't have any workout routines yet. Create one to get started!
      </p>
      <button
        onClick={() => openDialog("create-routine", { exercises: exercises })}
        className="px-4 py-2 bg-primary text-white rounded hover:bg-opacity-90 transition-colors"
      >
        Get Started
      </button>
    </div>
  ) : (
    <div className="flex flex-col gap-6 py-4">
      {untilLimit < 2 &&
        (untilLimit < 1 ? (
          <Banner
            visible
            variant="error"
            title="Routine limit reached"
            body="You will need to delete one of your existing routines before creating a new one."
          />
        ) : (
          <Banner
            visible
            variant="warning"
            title="Approaching routine limit"
            body={`You have ${untilLimit} routine${untilLimit > 1 ? "s" : ""} left to create.`}
          />
        ))}

      {routines.map((routine) => (
        <RoutineCard
          key={routine.id}
          routine={routine}
          exercises={exercisesMap}
          friends={friends}
          preferredVisibility={preferredVisibility}
        />
      ))}
    </div>
  );
};

export { RoutineList };
